// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Button,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { ChatHistory } from './ChatHistory';
import { UserMenu } from './UserMenu';

// Context for sidebar state
export const SidebarContext = createContext();

const SIDEBAR_WIDTH = 256;
const COLLAPSED_WIDTH = 64;
const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 520;
const SIDEBAR_WIDTH_KEY = 'sidebarWidth';

const clampSidebarWidth = (w) => Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(w)));

const readStoredSidebarWidth = () => {
  try {
    const stored = parseInt(window.localStorage.getItem(SIDEBAR_WIDTH_KEY), 10);
    return Number.isFinite(stored) ? clampSidebarWidth(stored) : SIDEBAR_WIDTH;
  } catch {
    return SIDEBAR_WIDTH;
  }
};

export const Sidebar = ({
  isOpen,
  isCollapsed,
  onClose,
  onCollapse,
  onExpand,
  onNewChat,
  onNewIncognitoChat,
  isIncognitoActive,
  onSearchChats,
  onSelectThread,
  threads,
  sharedThreads = [],
  currentThreadId,
  customGpts,
  user,
  onOpenProfile,
  onLogout,
  onOpenCompanyAdmin,
  apiUrl,
  onRenameThread,
  onDeleteThread,
  onLeaveThread,
  onPinThread,
  onShareThread
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Resizable sidebar (desktop): drag the right edge to widen; persisted.
  const [sidebarWidth, setSidebarWidth] = useState(readStoredSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const resizeState = useRef(null);

  const applyWidth = useCallback((px) => {
    setSidebarWidth(clampSidebarWidth(px));
  }, []);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    resizeState.current = { startX: e.clientX, startWidth: sidebarWidth };
    setIsResizing(true);
  }, [sidebarWidth]);

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e) => {
      if (!resizeState.current) return;
      applyWidth(resizeState.current.startWidth + (e.clientX - resizeState.current.startX));
    };
    const onUp = () => {
      setIsResizing(false);
      resizeState.current = null;
      setSidebarWidth((w) => {
        try { window.localStorage.setItem(SIDEBAR_WIDTH_KEY, String(w)); } catch { /* private mode */ }
        return w;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, applyWidth]);

  const resizeHandle = (
    <Box
      onMouseDown={handleResizeStart}
      onDoubleClick={() => { applyWidth(SIDEBAR_WIDTH); try { window.localStorage.removeItem(SIDEBAR_WIDTH_KEY); } catch { /* ignore */ } }}
      title="Drag to resize — double-click to reset"
      sx={{
        position: 'absolute',
        top: 0,
        right: -3,
        width: 6,
        height: '100%',
        cursor: 'col-resize',
        zIndex: 40,
        bgcolor: isResizing ? 'primary.main' : 'transparent',
        opacity: isResizing ? 0.4 : 1,
        '&:hover': { bgcolor: 'primary.main', opacity: 0.4 },
      }}
    />
  );

  const sidebarContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.nav',
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      {/* Desktop Header with Collapse Button */}
      {!isMobile && (
        <Box sx={{ display: 'flex', pl: '9px', pr: 1.5, pt: 1, pb: 1, justifyContent: 'flex-start' }}>
          <IconButton
            onClick={onCollapse}
            size="small"
            sx={{ color: 'text.secondary' }}
            title="Collapse sidebar"
          >
            <MenuOpenIcon />
          </IconButton>
        </Box>
      )}

      {/* Mobile Header with Close Button */}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Menu
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* New Chat Button */}
      <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
        <Button
          fullWidth
          startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={onNewChat}
          sx={{
            justifyContent: 'flex-start',
            pl: 1.5,
            pr: 1.5,
            py: 0.75,
            textTransform: 'none',
            fontWeight: 600,
            color: 'primary.main',
            '&:hover': {
              bgcolor: 'action.hover',
              color: 'primary.dark',
            },
          }}
        >
          New Chat
        </Button>
      </Box>

      {/* Incognito Chat Button — toggle. While active (purple), clicking it
          EXITS incognito and starts a new regular chat. */}
      {onNewIncognitoChat && (
        <Box sx={{ px: 1.5, pt: 0.5, pb: 0.5 }}>
          <Button
            fullWidth
            startIcon={<VisibilityOffIcon sx={{ fontSize: 18 }} />}
            onClick={onNewIncognitoChat}
            title={isIncognitoActive
              ? 'Exit incognito — start a new regular chat'
              : 'Start an incognito chat (hidden from history, no memory)'}
            sx={{
              justifyContent: 'flex-start',
              pl: 1.5,
              pr: 1.5,
              py: 0.75,
              textTransform: 'none',
              fontWeight: 600,
              color: isIncognitoActive ? '#7c3aed' : 'text.secondary',
              bgcolor: isIncognitoActive ? 'rgba(124, 58, 237, 0.12)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(124, 58, 237, 0.16)',
                color: '#7c3aed',
              },
            }}
          >
            {isIncognitoActive ? 'Exit Incognito' : 'Incognito Chat'}
          </Button>
        </Box>
      )}


      {/* Separator */}
      <Divider sx={{ mb: 1.5 }} />

      {/* Chat History */}
      <ChatHistory
        threads={threads}
        sharedThreads={sharedThreads}
        currentThreadId={currentThreadId}
        onSelectThread={onSelectThread}
        onCloseMobile={onClose}
        onRenameThread={onRenameThread}
        onDeleteThread={onDeleteThread}
        onLeaveThread={onLeaveThread}
        onSearchChats={onSearchChats}
        onPinThread={onPinThread}
        onNewChat={onNewChat}
        onShareThread={onShareThread}
        customGpts={customGpts}
      />

      {/* User Menu */}
      <UserMenu
        user={user}
        onOpenProfile={onOpenProfile}
        onLogout={onLogout}
        onOpenCompanyAdmin={onOpenCompanyAdmin}
      />
    </Box>
  );

  const collapsedContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: COLLAPSED_WIDTH,
        height: '100%',
        bgcolor: 'background.nav',
        borderRight: 1,
        borderColor: 'divider',
        pt: 1,
      }}
    >
      {/* Expand Button */}
      <IconButton
        onClick={onExpand}
        size="small"
        sx={{ mb: 1, color: 'text.secondary' }}
        title="Expand sidebar"
      >
        <ChevronRightIcon />
      </IconButton>

      {/* New Chat Icon */}
      <IconButton
        onClick={onNewChat}
        size="small"
        sx={{ mb: 1, color: 'primary.main', '&:hover': { bgcolor: 'action.hover', color: 'primary.dark' } }}
        title="New Chat"
      >
        <AddIcon />
      </IconButton>

      {/* Incognito Chat Icon — toggle (filled purple while active) */}
      {onNewIncognitoChat && (
        <IconButton
          onClick={onNewIncognitoChat}
          size="small"
          title={isIncognitoActive ? 'Exit incognito — start a new regular chat' : 'Start an incognito chat'}
          sx={{
            mb: 1,
            color: isIncognitoActive ? '#7c3aed' : 'text.secondary',
            bgcolor: isIncognitoActive ? 'rgba(124, 58, 237, 0.12)' : 'transparent',
            '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.16)', color: '#7c3aed' },
          }}
        >
          <VisibilityOffIcon />
        </IconButton>
      )}

      {/* Search Icon */}
      <IconButton
        onClick={onSearchChats}
        size="small"
        sx={{ color: 'text.secondary' }}
        title="Search Chats"
      >
        <SearchIcon />
      </IconButton>

      {/* User Menu — pinned to the bottom */}
      <UserMenu
        collapsed
        user={user}
        onOpenProfile={onOpenProfile}
        onLogout={onLogout}
        onOpenCompanyAdmin={onOpenCompanyAdmin}
      />
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={isOpen && isMobile}
        onClose={onClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Desktop Sidebar - Full */}
      {!isMobile && !isCollapsed && (
        <Box
          component="aside"
          id="sidebar"
          sx={{
            position: 'relative',
            width: sidebarWidth,
            flexShrink: 0,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {sidebarContent}
          {resizeHandle}
        </Box>
      )}

      {/* Desktop Sidebar - Collapsed */}
      {!isMobile && isCollapsed && (
        <Box
          component="aside"
          id="sidebarCollapsed"
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {collapsedContent}
        </Box>
      )}
    </>
  );
};
